import { useEffect, useState } from "react";
import { plainMath, type MathSegment } from "./questionMarkup";

/**
 * KaTeX, loaded only when it is needed.
 *
 * It is 273 KB of JavaScript plus a stylesheet and fonts. Roughly a quarter of
 * the bank uses maths at all, and an English or Biology student would be paying
 * for it on a Nigerian data plan and never seeing a fraction. So it is a
 * dynamic import: Vite splits it into its own chunk, the app boots without it,
 * and it is fetched in the background once — after which it is cached for the
 * session.
 *
 * Until it arrives, maths renders through `plainMath`, which is legible rather
 * than correct. That is the right trade for a second of loading: a student
 * reading `(x-5)/(x(x-1))` can still work, a student reading
 * `\frac{x-5}{x(x-1)}` cannot.
 */
/**
 * Only the call we make.
 *
 * `typeof import("katex")` also describes a self-referential `katex` property
 * the UMD build exposes and the ES entry point does not, so naming the one
 * method keeps the two shapes from disagreeing.
 */
interface Katex {
  renderToString(
    tex: string,
    options?: { displayMode?: boolean; throwOnError?: boolean; strict?: boolean | string },
  ): string;
}

let katexPromise: Promise<Katex | null> | null = null;
let katexModule: Katex | null = null;

export function loadKatex(): Promise<Katex | null> {
  if (katexModule) return Promise.resolve(katexModule);

  katexPromise ??= Promise.all([import("katex"), import("katex/dist/katex.min.css")])
    .then(([module]) => {
      katexModule = (module.default ?? module) as unknown as Katex;
      return katexModule;
    })
    .catch(() => {
      // Offline, or the chunk failed. The fallback still reads, so this is a
      // degraded render rather than a broken screen.
      katexPromise = null;
      return null;
    });

  return katexPromise;
}

/** Already loaded, for a synchronous first render with no flash. */
export function katexIfReady(): Katex | null {
  return katexModule;
}

function escape(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** The fallback renderer: readable maths, no dependency. */
export function renderMathPlain(segment: MathSegment): string {
  const text = escape(plainMath(segment.latex));
  return segment.display ? `<span class="math-display">${text}</span>` : `<span>${text}</span>`;
}

/** The real one. Falls back rather than throwing on malformed LaTeX. */
export function renderMathWith(katex: Katex | null): (segment: MathSegment) => string {
  if (!katex) return renderMathPlain;

  return (segment) => {
    try {
      return katex.renderToString(segment.latex, {
        displayMode: segment.display,
        // A bad expression in imported data must not take the screen down with
        // it; KaTeX renders what it can and marks the rest.
        throwOnError: false,
        strict: false,
      });
    } catch {
      return renderMathPlain(segment);
    }
  };
}

/**
 * Subscribe to the maths renderer.
 *
 * Returns null until KaTeX is available, so a caller renders the fallback
 * first and upgrades in place. `needed` is false for the majority of questions,
 * and then nothing is fetched at all.
 */
export function useKatex(needed: boolean): Katex | null {
  const [katex, setKatex] = useState<Katex | null>(katexIfReady);

  useEffect(() => {
    if (!needed || katex) return;

    let active = true;
    void loadKatex().then((loaded) => {
      if (active && loaded) setKatex(loaded);
    });

    return () => {
      active = false;
    };
  }, [needed, katex]);

  return katex;
}
